// record-checksum.ts
//
// Checksum của MỘT record journal theo §3.4: SHA-256 trên biểu diễn canonical của
// record TRỪ chính field checksum. Canonical JSON (key sắp theo code point, không
// khoảng trắng) đến từ P2 `canonical-json-and-checksum.ts` — P3 KHÔNG tạo lại.
//
// Vì sao phải bỏ field checksum khỏi đầu vào hash: nếu không, record đã seal không
// bao giờ verify lại được (hash của bản có checksum ≠ hash của bản chưa có).

import { canonicalJson, sha256Checksum } from "./canonical-json-and-checksum.js";

/** Tên field mang checksum trong record journal (spec §3.4). */
export const RECORD_CHECKSUM_FIELD = "event_checksum";

type Record_ = Record<string, unknown>;

function omitChecksumField(record: Record_): Record_ {
  const { [RECORD_CHECKSUM_FIELD]: _omitted, ...rest } = record;
  return rest;
}

/** SHA-256 hex trên canonical JSON của record đã bỏ field checksum. */
export function computeRecordChecksum(record: Record_): string {
  return sha256Checksum(canonicalJson(omitChecksumField(record)));
}

/** Đóng dấu record: trả bản sao có field checksum đúng. */
export function sealRecord<T extends Record_>(record: T): T & { event_checksum: string } {
  return { ...record, [RECORD_CHECKSUM_FIELD]: computeRecordChecksum(record) } as T & {
    event_checksum: string;
  };
}

/**
 * Record hợp lệ khi field checksum CÓ MẶT và khớp. Thiếu field ⇒ false (không
 * verify được ≠ hợp lệ — fail-closed, §3.4 cấm "bỏ qua record để chạy tiếp").
 */
export function verifyRecordChecksum(record: Record_): boolean {
  const declared = record[RECORD_CHECKSUM_FIELD];
  if (typeof declared !== "string" || declared.length === 0) return false;
  return declared === computeRecordChecksum(record);
}
